import { CountField } from '@/types/forms/fields/countField';
import { FormField } from '@/types/forms/fields/formField';
import { FormList } from '@/types/forms/fields/formList';
import { SelectField } from '@/types/forms/fields/selectField';
import { TextField } from '@/types/forms/fields/textField';
import { FormElement } from '@/types/forms/formElement';
import { ActivePowerAndApparentPowerFormGroup } from '@/types/forms/formGroups/plantPerformance/activePowerAndApparentPowerFormGroup';
import { GeneratorFormGroup } from '@/types/forms/formGroups/plantPerformance/generator/generatorFormGroup';
import { GeneratorModuleFormGroup } from '@/types/forms/formGroups/plantPerformance/generator/generatorModuleFormGroup';
import { QuantityAndGeneratorTypeFormGroup } from '@/types/forms/formGroups/plantPerformance/quantityAndGeneratorTypeFormGroup';
import { ManufacturerAndModuleTypeFormGroup } from '@/types/forms/formGroups/plantPerformance/manufacturerAndModuleTypeFormGroup';
import { FormOption } from '@/types/forms/formOption';
import { float } from '@/validators/float';
import { maxApparentPowerPlugInGeneratingPlant } from '@/validators/maxApparentPowerPlugInGeneratingPlant';
import { positive } from '@/validators/positive';
import { required } from 'vuelidate/lib/validators';
import { restrictedApparentPower } from './boundaryValues';
import { alwaysFalse } from '@/validators/alwaysFalse';
import { CheckboxField } from '@/types/forms/fields/checkboxField';
import { ZerezIdAndNormFormGroup } from '@/types/forms/formGroups/plantPerformance/zerezIdAndNormFormGroup';
import { distinct } from '@/utilities/utils';
import { contentServices } from '@/services/contentService';
import { HiddenFormField } from '@/types/forms/fields/hiddenFormField';
import { ConnectionRulesType } from '@/types/forms/shared/connectionRulesType';

export const generatorFormGroup = new GeneratorFormGroup({
    generatorHeadline: new FormElement(),
    generatorModules: new FormList(GeneratorModuleFormGroup, {
        initializeItems: 1,
        minItemCount: 1,
        maxItemCount: 5,
        id: new FormField<string>(),
        generatorManufacturerAndModuleType: new ManufacturerAndModuleTypeFormGroup(
            {
                manufacturer: new HiddenFormField<string>({
                    userChangeable: false,
                    validators: {
                        required,
                    },
                }),
                manufacturerText: new TextField({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    validators: {
                        required,
                    },
                }),
                manufacturerSelect: new SelectField<string>({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    hasServiceDrivenOptions: true,
                    onMountOptionsSetter: async () => {
                        const content = contentServices.form.content;
                        if (!content) {
                            throw 'Content unavailable';
                        }

                        const generatorsContent =
                            content.serviceDrivenContent.generatorsContent;
                        if (!generatorsContent) {
                            throw 'Generator Data unavailable';
                        }

                        const values = distinct(
                            generatorsContent.map(
                                (generator) => generator.manufacturerName,
                            ),
                        ) as string[];

                        return values.map(
                            (manufacturerName) =>
                                new FormOption({ key: manufacturerName }),
                        );
                    },
                    validators: {
                        required,
                    },
                }),
                moduleType: new HiddenFormField<string>({
                    userChangeable: false,
                    validators: {
                        required,
                    },
                }),
                moduleTypeText: new TextField({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    validators: {
                        required,
                    },
                }),
                moduleTypeSelect: new SelectField<string>({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    hasServiceDrivenOptions: true,
                    onMountOptionsSetter: async () => {
                        const content = contentServices.form.content;
                        if (!content) {
                            throw 'Content unavailable';
                        }

                        const generatorsContent =
                            content.serviceDrivenContent.generatorsContent;
                        if (!generatorsContent) {
                            throw 'Generator Data unavailable';
                        }

                        const values = distinct(
                            generatorsContent.map(
                                (generator) => generator.moduleType,
                            ),
                        ) as string[];

                        return values.map(
                            (manufacturerName) =>
                                new FormOption({ key: manufacturerName }),
                        );
                    },
                    validators: {
                        required,
                    },
                }),
            },
        ),
        generatorZerezIdAndNorm: new ZerezIdAndNormFormGroup({
            zerezId: new SelectField<string>({
                hasServiceDrivenOptions: true,
                onMountOptionsSetter: async () => {
                    const content = contentServices.form.content;
                    if (!content) {
                        throw 'Content unavailable';
                    }

                    const generatorsContent =
                        content.serviceDrivenContent.generatorsContent;
                    if (!generatorsContent) {
                        throw 'Generator Data unavailable';
                    }

                    const values = distinct(
                        generatorsContent.map((generator) => generator.zerezId),
                    ) as string[];

                    return values.map(
                        (zerezId) => new FormOption({ key: zerezId }),
                    );
                },
                validators: {
                    required,
                },
            }),
            norm: new HiddenFormField<ConnectionRulesType[]>({
                jsonSchema: {
                    type: 'array',
                    items: {
                        enum: Object.keys(ConnectionRulesType),
                    },
                },
            }),
            normText: new TextField({
                excludeFromDto: true,
                excludeFromSchema: true,
                userChangeable: false,
            }),
        }),
        generatorManualInputManufacturerTypeZerezId: new CheckboxField(),
        unitCertificateInvalidForInstalledPower: new FormElement({
            validators: {
                alwaysFalse,
            },
        }),
        generatorQuantityAndGeneratorType: new QuantityAndGeneratorTypeFormGroup(
            {
                quantity: new CountField({
                    value: 1,
                    min: 1,
                    validators: {
                        required,
                    },
                }),
                generatorType: new SelectField({
                    options: [
                        new FormOption({ key: 'type1' }),
                        new FormOption({ key: 'type2' }),
                    ],
                    validators: {
                        required,
                    },
                }),
            },
        ),
        generatorActivePowerAndApparentPower: new ActivePowerAndApparentPowerFormGroup(
            {
                activePower: new TextField({
                    validators: {
                        required,
                        float,
                        positive,
                    },
                }),
                apparentPower: new TextField({
                    validators: {
                        required,
                        float,
                        positive,
                        maxApparentPowerPlugInGeneratingPlant: maxApparentPowerPlugInGeneratingPlant(
                            restrictedApparentPower,
                        ),
                    },
                }),
            },
        ),
        generatorTotalActivePowerIsGreaterThanTotalApparentPowerWarning: new FormElement(
            {
                validators: {
                    alwaysFalse,
                },
            },
        ),
        generatorTotalActivePower: new FormField({
            value: 0,
        }),
        generatorTotalApparentPower: new FormField({
            value: 0,
        }),
    }),
    generatorOverallActivePower: new FormField({
        value: 0,
    }),
    generatorOverallApparentPower: new FormField({
        value: 0,
    }),
});
